import type { ContentPart, Prompt } from "@/context/prompt"

export type StoredPrompt = (Exclude<ContentPart, { type: "image" }> | {
  type: "image"
  id: string
  filename: string
  mime: string
  dataUrl?: string
  sourceUrl?: string
  attachment?: string
})[]
export type StoredAttachment = { key: string; blob: Blob; references: string[] }
export type EncodedPrompt = { prompt: StoredPrompt; blobs: Map<string, Blob>; bytes: number }

// The wire/editor contract still uses data URLs. Only metadata is serialized;
// binary payloads are decoded once per image while this repository is alive.
export function createPromptAttachmentCodec() {
  const cache = new Map<string, { url: string; key: string; blob: Blob }>()
  let cacheBytes = 0
  const clear = () => { cache.clear(); cacheBytes = 0 }

  const encode = async (prompt: Prompt): Promise<EncodedPrompt> => {
    const blobs = new Map<string, Blob>()
    const parts: StoredPrompt = []
    for (const part of prompt) {
      if (part.type !== "image" || !part.dataUrl.startsWith("data:")) {
        parts.push({ ...part })
        continue
      }
      let attachment = cache.get(part.id)
      if (!attachment || attachment.url !== part.dataUrl) {
        const match = /^data:([^,]*);base64,([\s\S]*)$/.exec(part.dataUrl)
        if (!match) throw new Error("An image attachment could not be saved: unsupported data URL.")
        const binary = atob(match[2])
        const bytes = Uint8Array.from(binary, (value) => value.charCodeAt(0))
        const digest = await crypto.subtle.digest("SHA-256", bytes)
        const key = `${part.mime}:${Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, "0")).join("")}`
        attachment = { url: part.dataUrl, key, blob: new Blob([bytes], { type: part.mime }) }
        // Bound this disposable conversion cache, not the durable attachment store.
        if (cacheBytes + bytes.length > 8 * 1024 * 1024) clear()
        if (bytes.length <= 8 * 1024 * 1024) {
          const previous = cache.get(part.id)
          if (previous) cacheBytes -= previous.blob.size
          cache.set(part.id, attachment)
          cacheBytes += bytes.length
        }
      }
      blobs.set(attachment.key, attachment.blob)
      const { dataUrl: _, ...metadata } = part
      parts.push({ ...metadata, attachment: attachment.key })
    }
    return {
      prompt: parts,
      blobs,
      bytes: new TextEncoder().encode(JSON.stringify(parts)).byteLength + [...blobs.values()].reduce((sum, blob) => sum + blob.size, 0),
    }
  }

  return { encode, clear }
}

export async function decodePrompt(prompt: StoredPrompt, blobs: Map<string, Blob>): Promise<Prompt> {
  return Promise.all(prompt.map(async (part): Promise<ContentPart> => {
    if (part.type !== "image") return part
    if (!part.attachment) {
      if (typeof part.dataUrl !== "string") throw new Error("A saved image attachment is missing.")
      return { ...part, dataUrl: part.dataUrl }
    }
    const blob = blobs.get(part.attachment)
    if (!blob) throw new Error("A saved image attachment is missing.")
    const bytes = new Uint8Array(await blob.arrayBuffer())
    const chunks: string[] = []
    for (let i = 0; i < bytes.length; i += 8192) chunks.push(String.fromCharCode(...bytes.subarray(i, i + 8192)))
    const dataUrl = `data:${part.mime};base64,${btoa(chunks.join(""))}`
    const { attachment: _, ...metadata } = part
    return { ...metadata, dataUrl }
  }))
}

export const attachmentKeys = (prompt: StoredPrompt) => [...new Set(prompt.flatMap((part) =>
  part.type === "image" && part.attachment ? [part.attachment] : [],
))]
